import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { GetAdsResponse, ServiceName } from '@/api/types/ads.types';
import { GET_ADS } from '@/api/queries/ads.queries';
import { COUNT_TO_FIND_DUPLICATES } from '@/hooks/useAdsWithInfiniteScroll/useAdsWithInfiniteScroll.constants';
import { UseAdsWithInfiniteScrollProps } from '@/hooks/useAdsWithInfiniteScroll/useAdsWithInfiniteScroll.types';
import { useQueryParam } from '@/hooks/useQueryParam';
import { QUERY } from '@/constants/common';
import { usePlatformSource } from '@/hooks/usePlatformSource';
import { AdsChunkSize } from '@/components/AdsWithInfiniteScroll/AdsWithInfiniteScroll.types';

export function useAdsWithInfiniteScroll({
  adKind,
  maxCount = 1000,
  ads,
  setAds,
  nextCursor,
  setNextCursor,
  chunkSize = AdsChunkSize.Middle,
  filters,
  keywords,
}: UseAdsWithInfiniteScrollProps) {
  const [isFinished, setIsFinished] = useState(false);
  const isLatest = adKind === ServiceName.Latest;
  const [keywordsSource] = useQueryParam(QUERY.keywords_source);
  const currentUrl = window.location.href;
  const source = usePlatformSource();

  const { data, loading: adsLoading } = useQuery<GetAdsResponse>(
    GET_ADS(adKind),
    {
      fetchPolicy: 'no-cache',
      variables: {
        adKind: !isLatest ? adKind : undefined,
        first: chunkSize,
        after: nextCursor,
        keywords,
        filters,
        keywordsSource: isLatest ? keywordsSource?.toUpperCase() : undefined,
        sourceLink: isLatest ? currentUrl : undefined,
        source,
      },
    }
  );

  const newAds = data?.ads?.nodes;
  const hasNextPage = data?.ads?.pageInfo?.hasNextPage;

  useEffect(() => {
    if (newAds?.length) {
      const lastAdsSet = new Set(
        ads.slice(-COUNT_TO_FIND_DUPLICATES).map(ad => ad.id)
      );
      const filteredNewAds = newAds?.filter(ad => !lastAdsSet.has(ad.id)) || [];
      setAds(
        !hasNextPage && !nextCursor.length
          ? newAds
          : [...ads, ...filteredNewAds]
      );
    }
    if (hasNextPage === false) {
      setIsFinished(true);
    }
  }, [data]);

  function onLoadMore() {
    if (hasNextPage) {
      setNextCursor(data?.ads?.pageInfo?.endCursor);
    }
  }

  useEffect(() => {
    setIsFinished(ads.length >= maxCount);
  }, [ads.length]);

  return { onLoadMore, isFinished, adsLoading };
}
